import { useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';

import {
  Button,
  ChipGroup,
  ChipInput,
  Flex,
  FormCheckbox,
  FormInput,
  FormInputIcon,
  GridList,
  GridListRow,
  IconSearch,
  IconX,
  Text,
} from '@spotify-internal/encore-web';
import { Popover, PopoverRoot } from '@spotify-internal/encore-web/next';
import {
  SearchUtils,
  useDebouncedSearch,
} from '@spotify/confidence-core-react';
import { SurfaceRefFragment } from '@spotify/confidence-graphql';
import { useSurfaces } from '@spotify/plugin-confidence-surfaces-react';

import styles from './IssueSurfacesPicker.module.css';

const CompactClearIcon = () => <IconX size="xsmall" aria-hidden />;

export type IssueSurfacesPickerProps = {
  surfaces: SurfaceRefFragment[];
  disabled?: boolean;
  layout?: 'stacked' | 'inline';
  onChange: (surfaceNames: string[]) => void;
};

type SurfaceOptionListProps = {
  surfaces: SurfaceRefFragment[];
  selectedSurfaceNames: Set<string>;
  onSelectionChange: (surfaceNames: Set<string>) => void;
  onClose: () => void;
};

const SurfaceOptionList = ({
  surfaces,
  selectedSurfaceNames,
  onSelectionChange,
  onClose,
}: SurfaceOptionListProps) => {
  const toggleSurface = (surfaceName: string, selected: boolean): void => {
    const nextSurfaceNames = new Set(selectedSurfaceNames);

    if (selected) {
      nextSurfaceNames.add(surfaceName);
    } else {
      nextSurfaceNames.delete(surfaceName);
    }

    onSelectionChange(nextSurfaceNames);
  };

  return (
    <GridList aria-label="Surfaces">
      {surfaces.map(surface => (
        <GridListRow
          key={surface.name}
          id={surface.name}
          textValue={surface.displayName}
          size="small"
          className={styles.surfaceRow}
        >
          <Flex
            alignItems="center"
            gap="tighter-2"
            className={styles.surfaceRowContent}
          >
            <FormCheckbox
              id={`surface-${surface.name}`}
              aria-label={`Toggle ${surface.displayName}`}
              size="small"
              checked={selectedSurfaceNames.has(surface.name)}
              onChange={event => {
                toggleSurface(surface.name, event.currentTarget.checked);
              }}
             data-track-source-anchor="4ba2508699;surface-option-list-form-checkbox"/>
            <button
              type="button"
              className={styles.surfaceAction}
              onClick={() => {
                if (!selectedSurfaceNames.has(surface.name)) {
                  toggleSurface(surface.name, true);
                }

                onClose();
              }}
             data-track-source-anchor="451f83318a;surface-option-list-button">
              <Text as="span" variant="bodySmall">
                {surface.displayName}
              </Text>
            </button>
          </Flex>
        </GridListRow>
      ))}
    </GridList>
  );
};

export type IssueSurfacesPickerOptionsProps = {
  selectedSurfaceNames: Set<string>;
  onChange: (surfaceNames: string[]) => void;
  onClose: () => void;
};

export const IssueSurfacesPickerOptions = ({
  selectedSurfaceNames,
  onChange,
  onClose,
}: IssueSurfacesPickerOptionsProps) => {
  const { query, search, setSearch } = useDebouncedSearch();
  const filter = SearchUtils.buildSearchFilter(query, 'displayName');
  const { surfaces, hasNextPage, isLoading, isLoadingNextPage, nextPage } =
    useSurfaces({
      filter,
      orderBy: 'displayName:asc',
      pageSize: 25,
    });

  return (
    <>
      <FormInputIcon iconLeading={<IconSearch size="small" />}>
        <FormInput
          aria-label="Search surfaces"
          placeholder="Search surfaces"
          size="small"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          value={search}
          onChange={event => {
            setSearch(event.currentTarget.value);
          }}
          className={styles.searchField}
         data-track-source-anchor="ec850a45e3"/>
      </FormInputIcon>
      <div className={styles.options}>
        <SurfaceOptionList
          surfaces={surfaces ?? []}
          selectedSurfaceNames={selectedSurfaceNames}
          onClose={onClose}
          onSelectionChange={surfaceNames => {
            onChange([...surfaceNames]);
          }}
        />
        {hasNextPage && (
          <InView
            className={styles.loadMoreSentinel}
            onChange={inView => {
              if (inView && !isLoadingNextPage) {
                void nextPage();
              }
            }}
           data-track-source-anchor="37f480bf43;issue-surfaces-picker-options-in-view"/>
        )}
      </div>
      {!isLoading && !isLoadingNextPage && (surfaces?.length ?? 0) === 0 && (
        <Text
          as="p"
          variant="bodySmall"
          semanticColor="textSubdued"
          className={styles.status}
        >
          No surfaces found
        </Text>
      )}
    </>
  );
};

export const IssueSurfacesPicker = ({
  surfaces,
  disabled = false,
  layout = 'stacked',
  onChange,
}: IssueSurfacesPickerProps) => {
  const [hasOpenedPicker, setHasOpenedPicker] = useState(false);
  const addSurfaceButtonRef = useRef<HTMLButtonElement>(null);
  const selectedSurfaceNames = new Set(surfaces.map(surface => surface.name));
  const removeSurface = (surfaceName: string): void => {
    onChange(
      surfaces
        .filter(surface => surface.name !== surfaceName)
        .map(surface => surface.name),
    );
  };
  const addSurfaceButton = (
    <Button
      ref={addSurfaceButtonRef}
      variant="tertiary"
      size="small"
      disabled={disabled}
      onClick={() => {
        setHasOpenedPicker(true);
      }}
     data-track-source-anchor="8679e045a6">
      + Add surface
    </Button>
  );
  const surfaceControls = (
    <ChipGroup
      mode="input"
      layout="multi-line"
      aria-label="Selected surfaces"
      className={styles.surfaceChips}
    >
      {surfaces.map(surface => {
        const surfaceChip = (
          <ChipInput
            key={surface.name}
            size={layout === 'inline' ? 'small' : undefined}
            clearIcon={CompactClearIcon}
            iconTrailingIsHidden={disabled}
            title={surface.displayName}
            onRemove={() => {
              removeSurface(surface.name);
              addSurfaceButtonRef.current?.focus();
            }}
            disabled={disabled}
            removeScreenReaderLabel={`Remove ${surface.displayName}`}
           data-track-source-anchor="5e9f7b9417;issue-surfaces-picker-chip-input">
            {surface.displayName}
          </ChipInput>
        );

        return layout === 'inline' ? (
          surfaceChip
        ) : (
          <div key={surface.name} className={styles.selectedSurfaceRow}>
            {surfaceChip}
          </div>
        );
      })}
    </ChipGroup>
  );

  const pickerControls = (
    <div className={styles.pickerControls} data-layout={layout}>
      {surfaces.length > 0 && surfaceControls}
      {addSurfaceButton}
    </div>
  );

  if (disabled) {
    return pickerControls;
  }

  return (
    <PopoverRoot>
      {pickerControls}
      <Popover
        aria-label="Change issue surfaces"
        placement="bottom"
        hasNoArrow
        className={styles.popover}
      >
        {({ close }) =>
          hasOpenedPicker ? (
            <IssueSurfacesPickerOptions
              selectedSurfaceNames={selectedSurfaceNames}
              onChange={onChange}
              onClose={close}
             data-track-source-anchor="7eaafceae8;issue-surfaces-picker-issue-surfaces-picker"/>
          ) : null
        }
      </Popover>
    </PopoverRoot>
  );
};
